'use client';

import { useMemo } from 'react';

import { useLocale, useTranslations } from 'next-intl';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getTextDirection } from '@/lib/utils/locale-shared';

interface LanguageSwitchDialogProps {
  detectedLocale: string;
  currentLocale: string;
  onConfirm: () => void;
  onDismiss: () => void;
}

export function LanguageSwitchDialog({
  detectedLocale,
  currentLocale,
  onConfirm,
  onDismiss,
}: LanguageSwitchDialogProps) {
  const t = useTranslations('languageDetection');
  const appLocale = useLocale();
  const dialogDir = getTextDirection(appLocale);

  const displayNames = useMemo(() => {
    try {
      return new Intl.DisplayNames([appLocale], { type: 'language' });
    } catch {
      return null;
    }
  }, [appLocale]);

  const getLanguageName = (code: string) => displayNames?.of(code) ?? code.toUpperCase();
  const detectedName = getLanguageName(detectedLocale);
  const currentName = getLanguageName(currentLocale);

  return (
    <Dialog open>
      <DialogContent
        role="alertdialog"
        showCloseButton={false}
        onPointerDownOutside={e => e.preventDefault()}
        onEscapeKeyDown={e => e.preventDefault()}
      >
        <div dir={dialogDir} className="flex flex-col gap-5">
          <DialogHeader>
            <DialogTitle className="text-xl">{t('title')}</DialogTitle>
            <DialogDescription className="flex flex-col gap-1.5 text-sm">
              <span>{t('notice', { language: detectedName })}</span>
              <span>{t('question')}</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={onDismiss}>
              {t('dismiss', { language: currentName })}
            </Button>
            <Button variant="accent" onClick={onConfirm} autoFocus>
              {t('confirm', { language: detectedName })}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
